/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable import/extensions */
import React, {
    CSSProperties,
    MouseEvent,
    useEffect,
    useRef,
    useState,
} from 'react'
import type {
    GroupByType,
    SwingTimingData,
} from 'leaderboard/scripts/swing-path/types/globals.ts'
import { color as d3Color } from 'd3'
import getShortPitchName from '../utils/get-short-pitch-name.ts'
import {
    getPitchTypeColor,
    getPitchTypeColorGrouping,
} from '../utils/pitch-type-colors'
import getImageUrl from './utils/get-image-url.ts'
import getPitcherType from './utils/get-pitcher-type.ts'
import type { GroupByLogic } from './utils/do-group-by-logic.ts'
import getDistributionColor from './utils/get-distribution-color.ts'
import { getKeyFromRow } from './Wrapper.tsx'
import type { PitchTypeColorMode } from './utils/can-use-pitch-type-colors.ts'
import type SwingTimingChart from './swing-timing-chart.ts'

function lightenHexColor(hexColor: string, amount = 0.25) {
    const d3ColorValue = d3Color(hexColor)

    if (!d3ColorValue) {
        return hexColor
    }

    return d3ColorValue.brighter(amount).formatRgb()
}

function getBatterType(row: SwingTimingData) {
    if (row.bat_side_type === 'R') {
        return 'RHH'
    }
    if (row.bat_side_type === 'L') {
        return 'LHH'
    }
    if (row.bat_side_type === 'X') {
        return 'SH'
    }

    return ''
}

type MobilePlayerCardChartProps = {
    row: SwingTimingData
    type: GroupByType
    pitchTypeColors: boolean
    pitchTypeColorMode: PitchTypeColorMode
    hoveredKey: string | null
    handleRemove: (row: SwingTimingData) => void
    groupByLogic: GroupByLogic
    charts: SwingTimingChart[]
}

export default function MobilePlayerCardChart({
    row,
    type,
    pitchTypeColors,
    pitchTypeColorMode,
    hoveredKey,
    handleRemove,
    groupByLogic,
    charts,
}: MobilePlayerCardChartProps) {
    const { hasGroupBys, extraGroupBys, groupByTitles, groupByDisplayFns } =
        groupByLogic
    const [isExpanded, setIsExpanded] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const imgSrc = getImageUrl(type, row)
    const pitchType03 =
        typeof row.api_pitch_type_group03 === 'string'
            ? row.api_pitch_type_group03
            : null
    const pitchType09 =
        typeof row.api_pitch_type_group09 === 'string'
            ? row.api_pitch_type_group09
            : null
    const pitchType =
        pitchTypeColorMode === 'group09' ? pitchType09 : pitchType03

    let color = getDistributionColor(getKeyFromRow(row))

    if (pitchTypeColors && pitchType !== null) {
        color =
            pitchTypeColorMode === 'group09'
                ? getPitchTypeColorGrouping(pitchType, false)
                : getPitchTypeColor(pitchType, false)
    }

    const isHovered = hoveredKey === row.uniqueId

    const containerStyle = {
        '--player-border-color': color,
        '--swing-timing-player-card-name-background': lightenHexColor(color),
    } as CSSProperties

    function handleMouseOver() {
        charts.forEach((chart) => {
            chart.setHoveredCurve(getKeyFromRow(row))
        })
    }

    function handleMouseOut() {
        charts.forEach((chart) => {
            chart.setHoveredCurve(null)
        })
    }

    function handleRemoveClick(e: MouseEvent<HTMLButtonElement>) {
        e.stopPropagation()
        handleRemove(row)
    }

    function handleCardClick() {
        setIsExpanded((prev) => !prev)
    }

    // Close expanded info when clicking outside
    useEffect(() => {
        if (!isExpanded) return undefined

        const handleClickOutside = (event: Event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsExpanded(false)
            }
        }

        document.addEventListener('click', handleClickOutside)
        return () => {
            document.removeEventListener('click', handleClickOutside)
        }
    }, [isExpanded])

    const hiddenGroupBy = type === 'batter' ? 'bat_side' : 'pitch_hand'
    const handednessLabel =
        type === 'batter' ? getBatterType(row) : getPitcherType(row)
    const shouldShowHandedness =
        type === 'batter' || row.pitch_hand_type !== 'X'

    return (
        <div
            ref={containerRef}
            key={row.uniqueId}
            className={`swing-timing-mobile-legend-item ${
                isExpanded ? 'expanded' : ''
            }`}
            style={containerStyle}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
            onFocus={handleMouseOver}
            onBlur={handleMouseOut}
            onClick={handleCardClick}
        >
            <button
                type="button"
                className="swing-timing-remove-player swing-timing-remove-player-mobile"
                aria-label={`Remove ${row.name}`}
                onClick={handleRemoveClick}
                onFocus={handleMouseOver}
                onBlur={handleMouseOut}
            >
                ✖
            </button>
            <img
                alt={row.name}
                src={imgSrc}
                className={`swing-timing-mobile-legend-image ${
                    isHovered ? 'hovered' : ''
                }`}
            />
            <div className="swing-timing-mobile-player-info">
                <div className="swing-timing-mobile-player-name">
                    {row.name}
                </div>
                {shouldShowHandedness && (
                    <div className="swing-timing-mobile-player-handedness">
                        {handednessLabel}
                    </div>
                )}
                {hasGroupBys && (
                    <div className="swing-timing-mobile-player-group-bys">
                        {extraGroupBys
                            .map((key, i) => {
                                if (key === 'api_pitch_type_group03') {
                                    return {
                                        key,
                                        label: getShortPitchName(row[key]),
                                    }
                                }

                                const label = groupByTitles[i]
                                const value = groupByDisplayFns[i](
                                    row[key],
                                    row
                                )

                                return { key, label: `${label}${value}` }
                            })
                            .filter(({ key }) => key !== 'pitch_hand')
                            .filter(({ key }) => key !== hiddenGroupBy)
                            .map(({ key, label }) => (
                                <div key={key}>{label}</div>
                            ))}
                    </div>
                )}
            </div>
        </div>
    )
}
